import React, { useState } from 'react';
import ConfirmModal from './ConfirmModal';
import { omapApi } from '../../../services/omapApi';

// Phase 7 flag metadata — hardcoded display info, matches backend flag keys
const FLAG_GROUPS = [
  {
    label: 'Foundation',
    flags: [
      { key: 'synthesis_engine', name: 'Synthesis Engine', description: 'Cross-session strategic memory consolidation', deps: [], tooltip: 'Builds a company intelligence dossier by consolidating insights across sessions over time' },
      { key: 'vigil_scheduler', name: 'Vigil Scheduler', description: 'Background company health monitoring', deps: [], tooltip: 'Runs periodic health checks — anomaly detection, vitals monitoring, and IVP drift alerts' },
      { key: 'swarm_dispatch', name: 'Swarm Dispatch', description: 'Parallel analysis worker dispatch', deps: [], tooltip: 'Sends strategic queries to multiple domain workers simultaneously for faster, broader analysis' },
    ],
  },
  {
    label: 'Intelligence',
    flags: [
      { key: 'process_intelligence_stores', name: 'Process Intelligence Stores', description: 'Living process knowledge with experiential learning', deps: [], tooltip: 'Creates a 4-layer knowledge store per process — foundation docs, experience, context, and synthesis' },
      { key: 'experience_accumulator', name: 'Experience Accumulator', description: 'Per-outcome confidence and pattern learning', deps: ['process_intelligence_stores'], tooltip: 'Tracks success/failure of actions to build confidence scores and detect temporal failure patterns' },
      { key: 'external_research', name: 'External Research', description: 'Periodic industry/regulatory research', deps: ['vigil_scheduler', 'process_intelligence_stores'], tooltip: 'Vigil periodically researches industry trends and regulatory changes to enrich process context' },
    ],
  },
  {
    label: 'Agency',
    flags: [
      { key: 'agency_governance', name: 'Agency Governance', description: 'Progressive autonomy governance framework', deps: [], tooltip: 'Enables the action pipeline — Causeway can evaluate and recommend actions based on autonomy levels' },
      { key: 'agency_l2_draft', name: 'L2: Draft & Await', description: 'Actions drafted for human approval', deps: ['agency_governance'], tooltip: 'Causeway drafts actions and queues them for your approval before anything is executed' },
      { key: 'agency_l3_execute', name: 'L3: Act & Notify', description: 'Autonomous execution within safety limits', deps: ['agency_l2_draft'], tooltip: 'Causeway can auto-execute actions within your safety thresholds and notify you afterward' },
    ],
  },
];

const ALL_FLAGS = FLAG_GROUPS.flatMap((g) => g.flags);

/** Get display names for dependency keys */
function depNames(depKeys: string[]): string {
  return depKeys
    .map((k) => ALL_FLAGS.find((f) => f.key === k)?.name || k)
    .join(', ');
}

/** Find all flags that depend on the given flag (directly or transitively) */
function findDependents(flagKey: string): string[] {
  const result: string[] = [];
  const queue = [flagKey];
  while (queue.length) {
    const current = queue.shift()!;
    for (const f of ALL_FLAGS) {
      if (f.deps.includes(current) && !result.includes(f.key)) {
        result.push(f.key);
        queue.push(f.key);
      }
    }
  }
  return result;
}

interface FeaturesSectionProps {
  companyId: number;
  flagStates: Record<string, boolean>;
  onFlagStatesChange: (states: Record<string, boolean>) => void;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({
  companyId,
  flagStates,
  onFlagStatesChange,
}) => {
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  function areDepsEnabled(deps: string[]): boolean {
    return deps.every((d) => flagStates[d] === true);
  }

  function handleToggle(flagKey: string) {
    const newValue = !flagStates[flagKey];
    const next = { ...flagStates, [flagKey]: newValue };

    // If turning OFF, also turn off all dependents
    if (!newValue) {
      const dependents = findDependents(flagKey);
      for (const dep of dependents) {
        next[dep] = false;
      }
    }

    onFlagStatesChange(next);
  }

  async function handleSave() {
    setShowConfirm(false);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const promises = ALL_FLAGS.map((flag) => {
        const enabled = flagStates[flag.key] ?? false;
        return omapApi.setCompanyFlag(companyId, flag.key, enabled);
      });
      await Promise.all(promises);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-section">
      <p className="settings-description">
        Enable or disable Progressive Autonomy capabilities for this company.
        Some features depend on others — disabled dependencies are shown.
      </p>

      {FLAG_GROUPS.map((group) => (
        <div key={group.label} className="settings-flag-group">
          <div className="settings-group-label">{group.label}</div>
          {group.flags.map((flag) => {
            const enabled = flagStates[flag.key] ?? false;
            const depsOk = areDepsEnabled(flag.deps);
            const disabled = !depsOk;

            return (
              <div
                key={flag.key}
                className={`settings-flag-row ${disabled ? 'settings-flag-disabled' : ''}`}
              >
                <div className="settings-flag-info">
                  <div className="settings-flag-name" data-tooltip={flag.tooltip}>{flag.name}</div>
                  <div className="settings-flag-desc">{flag.description}</div>
                  {flag.deps.length > 0 && !depsOk && (
                    <div className="settings-flag-dep-warning">
                      Requires: {depNames(flag.deps)}
                    </div>
                  )}
                </div>
                <button
                  className={`settings-toggle ${enabled ? 'settings-toggle-on' : ''}`}
                  onClick={() => !disabled && handleToggle(flag.key)}
                  disabled={disabled}
                  aria-label={`Toggle ${flag.name}`}
                >
                  <span className="settings-toggle-knob" />
                </button>
              </div>
            );
          })}
        </div>
      ))}

      {saveError && <div className="settings-error">{saveError}</div>}
      {saveSuccess && <div className="settings-success">Settings saved successfully.</div>}

      <div className="settings-save-bar">
        <button
          className="settings-btn settings-btn-primary"
          onClick={() => setShowConfirm(true)}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {showConfirm && (
        <ConfirmModal
          title="Confirm Changes"
          message="Are you sure you want to save these settings? Changes will take effect immediately."
          onConfirm={handleSave}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
};

export default FeaturesSection;
